import PropTypes from 'prop-types';
import { forwardRef, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUnreadCount, fetchNotifications } from 'store/features/notificationSlice';
import { enqueueSnackbar, closeSnackbar } from 'notistack';
import { Box, Text } from '@mantine/core';
import { IconBellRinging } from '@tabler/icons-react';
import { radius, semanticColors, shadows } from 'theme';

const POLL_INTERVAL = 30_000;

// "Open", not "Tap": the bell is clicked as often as it is tapped.
const TOAST_COPY = {
  title: (count) => (count === 1 ? '1 new notification' : `${count} new notifications`),
  hint: 'Open the bell to see them.'
};

// -- Custom snackbar content for new-notification toast --
// A raised surface with a full hairline border, like the bell's own popover;
// the coral chip carries the brand, so there is no accent stripe.
const NewNotificationSnackbar = forwardRef(({ id, count }, ref) => (
  <Box
    ref={ref}
    onClick={() => closeSnackbar(id)}
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      backgroundColor: semanticColors.surface.raised,
      border: `1px solid ${semanticColors.border.default}`,
      borderRadius: radius.surface,
      boxShadow: shadows.popover,
      padding: '10px 16px',
      minWidth: 260,
      maxWidth: 340,
      cursor: 'pointer'
    }}
  >
    <Box
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 36,
        height: 36,
        borderRadius: radius.pill,
        backgroundColor: semanticColors.brand.coralSubtleBg,
        color: semanticColors.brand.coralText,
        flexShrink: 0
      }}
    >
      <IconBellRinging size={20} aria-hidden />
    </Box>
    <Box style={{ minWidth: 0 }}>
      <Text
        style={{
          fontSize: '0.84rem',
          fontWeight: 600,
          lineHeight: 1.3,
          color: semanticColors.text.primary,
          letterSpacing: '-0.01em'
        }}
      >
        {TOAST_COPY.title(count)}
      </Text>
      {/* text.secondary, not muted: muted is under 4.5:1 on surface.raised at this size. */}
      <Text
        style={{
          fontSize: '0.72rem',
          fontWeight: 400,
          lineHeight: 1.3,
          color: semanticColors.text.secondary,
          marginTop: 2
        }}
      >
        {TOAST_COPY.hint}
      </Text>
    </Box>
  </Box>
));

NewNotificationSnackbar.displayName = 'NewNotificationSnackbar';
NewNotificationSnackbar.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  count: PropTypes.number.isRequired
};

// -- Hook --
const useNotificationPolling = () => {
  const dispatch = useDispatch();
  const isLoggedIn = useSelector((state) => state.user.isLoggedIn);
  const { unreadCount, filter } = useSelector((state) => state.notification);
  const intervalRef = useRef(null);
  const prevCountRef = useRef(null);

  // Track count changes — show snackbar and refresh the notification list
  useEffect(() => {
    if (prevCountRef.current !== null && unreadCount > prevCountRef.current) {
      const diff = unreadCount - prevCountRef.current;

      enqueueSnackbar({
        autoHideDuration: 4000,
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
        content: (key) => <NewNotificationSnackbar id={key} count={diff} />
      });

      // Refresh the notification list so it's up to date when the user opens the bell
      dispatch(fetchNotifications({ filter }));
    }
    prevCountRef.current = unreadCount;
  }, [unreadCount, dispatch, filter]);

  useEffect(() => {
    if (!isLoggedIn) return;

    const poll = () => dispatch(fetchUnreadCount());

    // Initial fetch
    poll();

    const startPolling = () => {
      if (!intervalRef.current) {
        intervalRef.current = setInterval(poll, POLL_INTERVAL);
      }
    };

    const stopPolling = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        poll();
        startPolling();
      } else {
        stopPolling();
      }
    };

    startPolling();
    document.addEventListener('visibilitychange', handleVisibility);

    // eslint-disable-next-line consistent-return
    return () => {
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [dispatch, isLoggedIn]);
};

export default useNotificationPolling;
